<template>
    <AuthLayout v-bind="appSharedData.getAuthLayoutProps()">
        <template #title>Sign in to your account</template>

        <template #card-content>
            <div v-if="hasSocialLoginOptions">
                <div class="mt-6 space-y-4">
                    <GoogleSocialAuthButton
                        v-if="googleSocialLoginEnabled"
                        :url="urlFactory.makeGoogleOAuthLoginRedirectUrl()"
                    >
                        Sign in with Google
                    </GoogleSocialAuthButton>

                    <MicrosoftSocialAuthButton
                        v-if="microsoftSocialLoginEnabled"
                        :url="urlFactory.makeMicrosoftOAuthLoginRedirectUrl()"
                    >
                        Sign in with Microsoft
                    </MicrosoftSocialAuthButton>
                </div>

                <div class="relative mt-4">
                    <div class="absolute inset-0 flex items-center">
                        <div class="w-full border-t border-gray-200"></div>
                    </div>
                    <div
                        class="relative flex justify-center text-sm font-medium leading-6"
                    >
                        <span class="bg-white px-6 text-gray-900">Or</span>
                    </div>
                </div>
            </div>

            <ApiForm
                :url="urlFactory.makeRelativeAuthLoginPageUrl(destinationUrl)"
                method="POST"
                ref="apiFormRef"
            >
                <div>
                    <FormControl
                        label="Email"
                        :error="
                            apiFormRef?.response?.getValidationError(
                                RequestBodyKeyEnum.Auth_User_Login_Email,
                            )
                        "
                    >
                        <TextInput
                            type="email"
                            class="w-full"
                            :name="RequestBodyKeyEnum.Auth_User_Login_Email"
                            required
                        />
                    </FormControl>
                </div>

                <div>
                    <FormControl label="Password">
                        <TextInput
                            type="password"
                            class="w-full"
                            :name="RequestBodyKeyEnum.Auth_User_Login_Password"
                            required
                        />
                    </FormControl>
                </div>

                <div>
                    <Button
                        @click="apiFormRef?.submit()"
                        class="w-full mt-4"
                        :loading="apiFormRef?.loading.valueOf()"
                    >
                        Sign In
                    </Button>
                </div>

                <div class="flex flex-col mt-4 space-y-1">
                    <div class="text-sm leading-6">
                        <a
                            :href="urlFactory.makeAuthRegisterPageUrl()"
                            class="font-semibold text-primary hover:text-primary-shade"
                        >
                            Don't have an account?
                        </a>
                    </div>
                    <div class="text-sm leading-6">
                        <a
                            :href="urlFactory.makeAuthForgotPasswordPageUrl()"
                            class="font-semibold text-primary hover:text-primary-shade"
                        >
                            Forgot your password?
                        </a>
                    </div>
                </div>
            </ApiForm>
        </template>
    </AuthLayout>
</template>

<script setup lang="ts">
    import { ref } from 'vue'
    import AuthLayout from '@/Shared/Layout/App/Auth/AuthLayout.vue'
    import FormControl from '@/Shared/Component/Forms/FormControl.vue'
    import TextInput from '@/Shared/Component/Forms/TextInput.vue'
    import Button from '@/Shared/Component/Button.vue'
    import ApiForm from '@/Shared/Component/Forms/ApiForm.vue'
    import { RequestBodyKeyEnum } from '@/Shared/AutoGenerated/RequestFieldEnum'
    import type { ApiFormRefType } from '@/Shared/AutoGenerated/Types'
    import { UrlFactory } from '@/Shared/Url/UrlFactory'
    import GoogleSocialAuthButton from '@/App/User/Component/GoogleSocialAuthButton.vue'
    import MicrosoftSocialAuthButton from '@/App/User/Component/MicrosoftSocialAuthButton.vue'
    import AppSharedData from '@/Shared/Entity/AppSharedData'

    interface ShowLoginProps {
        appSharedDataData: any
        googleSocialLoginEnabled: boolean
        microsoftSocialLoginEnabled: boolean
        destinationUrl: string
    }

    const props = defineProps<ShowLoginProps>()
    const urlFactory = new UrlFactory()
    const appSharedData = AppSharedData.hydrate(props.appSharedDataData)
    const apiFormRef = ref<ApiFormRefType>(null)
    const hasSocialLoginOptions =
        props.googleSocialLoginEnabled || props.microsoftSocialLoginEnabled
</script>
